import { useEffect, useState } from 'react'

export interface UseCountdownResult {
	secondsLeft: number
	isExpired: boolean
	/** Remaining time formatted as mm:ss (clamped at 00:00) */
	formatted: string
}

const TICK_MS = 1_000

function computeSecondsLeft(expiresAt: string | null): number {
	if (!expiresAt) return 0
	const remainingMs = new Date(expiresAt).getTime() - Date.now()
	return Math.max(0, Math.ceil(remainingMs / 1000))
}

function formatSeconds(secondsLeft: number): string {
	const minutes = Math.floor(secondsLeft / 60)
	const seconds = secondsLeft % 60
	return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

/**
 * Counts down to an absolute timestamp. Remaining time is re-derived from
 * `Date.now()` on every tick (never decremented), so it stays correct across
 * timer drift and backgrounded tabs.
 */
export function useCountdown(expiresAt: string | null): UseCountdownResult {
	const [secondsLeft, setSecondsLeft] = useState<number>(() => computeSecondsLeft(expiresAt))

	useEffect(() => {
		setSecondsLeft(computeSecondsLeft(expiresAt))
		if (!expiresAt) return
		const interval = setInterval(() => {
			setSecondsLeft(computeSecondsLeft(expiresAt))
		}, TICK_MS)
		return () => clearInterval(interval)
	}, [expiresAt])

	return {
		secondsLeft,
		isExpired: !!expiresAt && secondsLeft <= 0,
		formatted: formatSeconds(secondsLeft),
	}
}
